import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Shield, CheckCircle2, Clock, XCircle, Upload, FileText } from "lucide-react";

const DOC_TYPES = [
  { id: "gst", label: "GST certificate" },
  { id: "registration", label: "Business registration" },
  { id: "license", label: "Practice / trade license" },
  { id: "id_proof", label: "Owner ID proof (Aadhaar / PAN)" },
  { id: "other", label: "Other supporting doc" },
];

const ENTITY_FOR_ROLE = { vet: "vet", store_owner: "store", hostel_owner: "hostel", ngo: "ngo" };

export default function Kyc() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ entity_type: "", doc_type: "gst", doc_name: "", doc_url: "" });
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => axios.get(`${API}/partner/kyc/my`).then(r => {
    setData(r.data);
    if (r.data.entities?.[0]) setForm(f => ({ ...f, entity_type: r.data.entities[0].entity_type }));
  });
  useEffect(() => { if (user) load(); }, [user]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setMsg("File too large (max 2MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, doc_url: reader.result, doc_name: f.doc_name || file.name }));
    reader.readAsDataURL(file);
  };

  const upload = async (e) => {
    e.preventDefault();
    setUploading(true); setMsg("");
    try {
      await axios.post(`${API}/partner/kyc/upload`, form);
      setMsg("Document uploaded — pending admin review.");
      setForm({ entity_type: form.entity_type, doc_type: "gst", doc_name: "", doc_url: "" });
      load();
      setTimeout(() => setMsg(""), 3500);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  if (!data) return <div className="text-ink-soft">Loading…</div>;
  if (data.entities.length === 0) {
    return <div className="bg-surface border border-line rounded-3xl p-8 text-center" data-testid="kyc-no-entity">
      <Shield className="w-10 h-10 text-brand mx-auto mb-3"/>
      <h2 className="font-heading text-2xl font-extrabold">No vendor profile yet</h2>
      <p className="text-ink-soft mt-1">Become a partner first (Vet / Store / Hostel / NGO) — then upload KYC docs to get verified.</p>
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="kyc-page">
      <header>
        <h1 className="font-heading text-3xl font-extrabold flex items-center gap-2"><Shield className="w-7 h-7 text-brand"/> KYC verification</h1>
        <p className="text-ink-soft text-sm">Upload documents to get the verified badge. Admin will review within 24 hours.</p>
      </header>

      {data.entities.map(ent => (
        <section key={ent.entity_id} className="bg-surface border border-line rounded-3xl p-6 shadow-soft" data-testid={`kyc-entity-${ent.entity_type}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-ink-soft">{ent.entity_type}</div>
              <div className="font-heading text-xl font-extrabold">{ent.name}</div>
            </div>
            {ent.approved ? (
              <span className="bg-leaf/30 text-ink px-3 py-1 rounded-full text-xs font-bold inline-flex items-center gap-1" data-testid={`kyc-approved-${ent.entity_type}`}><CheckCircle2 className="w-4 h-4 text-leaf"/> Verified</span>
            ) : (
              <span className="bg-sunshine/40 text-ink px-3 py-1 rounded-full text-xs font-bold inline-flex items-center gap-1"><Clock className="w-4 h-4"/> Pending review</span>
            )}
          </div>

          {ent.entity_type === "ngo" && <NGO80GSection ngo={ent}/>}

          {ent.kyc_documents.length > 0 && (
            <ul className="mt-4 space-y-2">
              {ent.kyc_documents.map(d => (
                <li key={d.doc_id} className="bg-surface-muted rounded-2xl p-3 flex items-center justify-between gap-3" data-testid={`kyc-doc-${d.doc_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-brand shrink-0"/>
                    <div className="min-w-0">
                      <div className="font-bold truncate">{d.doc_name}</div>
                      <div className="text-xs text-ink-soft">{DOC_TYPES.find(x => x.id === d.doc_type)?.label || d.doc_type} · uploaded {new Date(d.uploaded_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${d.status === "approved" ? "bg-leaf/30" : d.status === "rejected" ? "bg-danger/20 text-danger" : "bg-sunshine/40"}`}>{d.status}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <form onSubmit={upload} className="bg-surface border border-line rounded-3xl p-6 shadow-soft space-y-3" data-testid="kyc-upload-form">
        <h3 className="font-heading text-lg font-bold inline-flex items-center gap-2"><Upload className="w-5 h-5 text-brand"/> Upload a document</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">For business</span>
            <select required value={form.entity_type} onChange={e => setForm({...form, entity_type: e.target.value})} className="mt-1.5 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 font-bold outline-none focus:border-brand" data-testid="kyc-entity-select">
              {data.entities.map(e => <option key={e.entity_type} value={e.entity_type}>{e.name} ({e.entity_type})</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Document type</span>
            <select value={form.doc_type} onChange={e => setForm({...form, doc_type: e.target.value})} className="mt-1.5 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 font-bold outline-none focus:border-brand" data-testid="kyc-doc-type">
              {DOC_TYPES.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Display name</span>
            <input required value={form.doc_name} onChange={e => setForm({...form, doc_name: e.target.value})} className="mt-1.5 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 font-bold outline-none focus:border-brand" placeholder="e.g. GST_certificate_2026.pdf" data-testid="kyc-doc-name"/>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">File (PDF/JPG, max 2MB)</span>
            <input required type="file" accept="image/*,application/pdf" onChange={handleFile} className="mt-1.5 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 text-sm" data-testid="kyc-doc-file"/>
          </label>
        </div>
        {msg && <div className="text-sm font-bold text-leaf">{msg}</div>}
        <button type="submit" disabled={uploading || !form.doc_url} data-testid="kyc-upload" className="bg-brand text-white rounded-full px-6 py-3 font-bold disabled:opacity-50 hover:bg-brand-hover">{uploading ? "Uploading…" : "Upload document"}</button>
      </form>
    </div>
  );
}

function NGO80GSection() {
  const [form, setForm] = useState({ has_80g: false, certificate_number: "", certificate_url: "", valid_until: "" });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return alert("Max 2MB");
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, certificate_url: reader.result }));
    reader.readAsDataURL(file);
  };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await axios.patch(`${API}/partner/ngo/80g`, form);
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2500);
  };
  return (
    <form onSubmit={submit} className="mt-5 pt-5 border-t border-line" data-testid="ngo-80g-form">
      <div className="text-xs font-bold uppercase tracking-wider text-brand">80G Tax Exemption</div>
      <p className="text-sm text-ink-soft mt-1">Adding your 80G certification unlocks the <b>"Tax-deductible"</b> badge on your NGO page — donors typically give 2-3x more to verified-80G NGOs.</p>
      <label className="flex items-center gap-2 mt-3 text-sm">
        <input data-testid="has-80g" type="checkbox" checked={form.has_80g} onChange={e => setForm({...form, has_80g: e.target.checked})}/>
        Our NGO is 80G certified
      </label>
      {form.has_80g && (
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">Certificate number *</span>
            <input data-testid="cert-number" required value={form.certificate_number} onChange={e => setForm({...form, certificate_number: e.target.value})} className="mt-1 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 font-bold outline-none focus:border-brand"/>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">Valid until</span>
            <input type="date" value={form.valid_until} onChange={e => setForm({...form, valid_until: e.target.value})} className="mt-1 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 font-bold outline-none focus:border-brand"/>
          </label>
          <label className="block md:col-span-2">
            <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">80G certificate file (PDF/JPG, max 2MB)</span>
            <input data-testid="cert-file" type="file" accept="image/*,application/pdf" onChange={onFile} className="mt-1 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 text-sm"/>
            {form.certificate_url && <div className="text-[11px] text-brand-green font-bold mt-1">✓ File attached</div>}
          </label>
        </div>
      )}
      <button data-testid="save-80g" className="mt-4 bg-ink text-white rounded-full px-5 py-2 text-sm font-bold">{saving ? "Saving…" : saved ? "Saved ✓" : "Save 80G info"}</button>
    </form>
  );
}
